/**
 * RoleExplainerModal — lecteur plein écran, pensé mobile d'abord, qui présente
 * le rôle choisi (Parrain, Relais, Bénéficiaire) sous forme de petites scènes
 * animées + sous-titres + narration vocale.
 *
 * - Voix : synthèse vocale de l'appareil (via useNarration), avec repli minuté
 *   si la voix est indisponible (les sous-titres défilent quand même).
 * - Sous-titres toujours visibles (fiabilité + lecture pour tous).
 * - Accessibilité : role="dialog", focus trap, Échap pour fermer, verrouillage
 *   du défilement de l'arrière-plan, cibles tactiles ≥ 44px.
 * - La lecture démarre au tap (ouverture) → compatible politiques d'autoplay.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getRoleContent,
  getExplainerModalUi,
  isLanguageAvailable,
  LANGUAGES,
  DEFAULT_EXPLAINER_LANG,
  BG_MUSIC_SRC,
} from './onboarding.content.js';
import SceneCrossfade from './SceneCrossfade.jsx';
import { useNarration } from '../../hooks/useNarration.js';
import { useBackgroundMusic } from '../../hooks/useBackgroundMusic.js';
import './RoleExplainerModal.css';

const FOCUSABLE = 'button, a[href], [tabindex]:not([tabindex="-1"])';

const normalize = (w) => (w || '').toLowerCase().replace(/[^\p{L}\p{N}-]/gu, '');

/**
 * Sous-titre karaoké : surligne les mots au fil de la progression (0→1) et met
 * le mot-clé en or. Toujours entièrement lisible (les mots « à venir » sont juste
 * atténués), pour l'accessibilité et les lecteurs peu à l'aise.
 */
function KaraokeCaption({ text, keyword, progress }) {
  const tokens = (text || '').trim().split(/\s+/).filter(Boolean);
  const spokenCount = Math.round(progress * tokens.length);
  const key = normalize(keyword);
  return (
    <>
      {tokens.map((token, i) => {
        const isSpoken = i < spokenCount;
        const isKeyword = key && normalize(token) === key;
        const cls = [
          'rx-word',
          isSpoken ? 'is-spoken' : '',
          isKeyword ? 'is-keyword' : '',
        ].filter(Boolean).join(' ');
        return (
          <span key={`${token}-${i}`} className={cls}>
            {token}
            {i < tokens.length - 1 ? ' ' : ''}
          </span>
        );
      })}
    </>
  );
}

function RoleExplainerModal({ roleKey, open, onClose, defaultLang = DEFAULT_EXPLAINER_LANG }) {
  const navigate = useNavigate();
  const narration = useNarration();
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  // Désarmé à chaque ouverture : évite que l'effet pause/reprise ne déclenche un
  // `pause()` parasite au tout premier rendu (où `playing` est encore périmé à
  // `false`), ce qui interrompait l'audio à peine lancé (→ deux voix / à-coup).
  const pauseResumeArmedRef = useRef(false);

  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [lang, setLang] = useState(defaultLang);

  // Musique de fond en boucle (faible volume), sous la narration. Coupée avec le son.
  useBackgroundMusic(BG_MUSIC_SRC, { play: open && playing && !muted, volume: 0.14 });

  // Mémorisé : getRoleContent recrée les objets de scène (map) à chaque appel.
  // Sans cela, la progression (setProgress ~60x/s) rerendrait le modal, changerait
  // l'identité de `scene`, et ferait « thrasher » l'effet de lecture (scènes qui
  // défilent trop vite). La clé (rôle + langue) garde une identité stable.
  const content = useMemo(
    () => (roleKey ? getRoleContent(roleKey, lang) : null),
    [roleKey, lang],
  );
  const ui = useMemo(() => getExplainerModalUi(content?.lang || lang), [content?.lang, lang]);
  const scenes = content?.scenes || [];
  const total = scenes.length;
  const scene = scenes[index] || null;
  const isLast = index >= total - 1;
  // « Fini » est un état explicite : distinguer la fin (→ appel à l'action) d'une
  // simple pause sur la dernière scène (qui doit rester une pause, pas le CTA).
  const atEnd = ended;

  // (Ré)initialise à l'ouverture ; nettoie à la fermeture.
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement;
      pauseResumeArmedRef.current = false;
      setIndex(0);
      const startLang = roleKey && isLanguageAvailable(roleKey, defaultLang)
        ? defaultLang
        : 'fr';
      setLang(startLang);
      setEnded(false);
      setPlaying(true);
    } else {
      narration.stop();
      setPlaying(false);
      // Rend le focus à l'élément déclencheur.
      if (previousFocusRef.current?.focus) {
        previousFocusRef.current.focus();
      }
    }
    // narration.stop est stable (useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Verrouille le défilement de l'arrière-plan + gère Échap et le focus trap.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const nodes = dialogRef.current?.querySelectorAll(FOCUSABLE);
        if (!nodes || nodes.length === 0) return;
        const focusable = Array.from(nodes).filter((n) => !n.disabled && n.offsetParent !== null);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Place le focus dans le dialog à l'ouverture.
  useEffect(() => {
    if (open && dialogRef.current) {
      const firstBtn = dialogRef.current.querySelector('.rx-close');
      firstBtn?.focus();
    }
  }, [open]);

  // Précharge l'illustration de la scène suivante (transition fluide).
  useEffect(() => {
    if (!open) return;
    const next = scenes[index + 1];
    if (next?.image) {
      const img = new Image();
      img.src = next.image;
    }
  }, [open, index, scenes]);

  // Démarre (ou redémarre) la scène courante quand elle change : ouverture,
  // navigation, changement de langue ou de sourdine. NE dépend PAS de `playing`
  // pour ne pas repartir de zéro lors d'une pause/reprise.
  useEffect(() => {
    if (!open || !scene) return undefined;
    const onEnd = () => {
      if (index < total - 1) {
        setIndex(index + 1);
      } else {
        setPlaying(false);
        setEnded(true);
      }
    };
    narration.play(scene, { onEnd, muted });
    return () => narration.stop();
    // narration.play/stop sont stables (useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, muted, scene, total]);

  // Pause / reprise réelle : conserve la position (pas de redémarrage de scène).
  // Le tout premier passage après l'ouverture est ignoré : l'effet de scène
  // ci-dessus vient de lancer la narration, et `playing` peut encore valoir
  // `false` (le setPlaying(true) d'ouverture n'est pas encore appliqué). Sans
  // ce garde, on appelait pause() sur l'audio à peine démarré → sa promesse
  // play() se rejetait (AbortError) et la voix de repli se superposait (2 voix).
  useEffect(() => {
    if (!open) return;
    if (!pauseResumeArmedRef.current) {
      pauseResumeArmedRef.current = true;
      return;
    }
    if (playing) narration.resume();
    else narration.pause();
    // narration.pause/resume sont stables (useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, open]);

  if (!open || !content) return null;

  const goPrev = () => {
    setEnded(false);
    setIndex((i) => Math.max(0, i - 1));
    setPlaying(true);
  };
  const goNext = () => {
    if (isLast) return;
    setEnded(false);
    setIndex((i) => Math.min(total - 1, i + 1));
    setPlaying(true);
  };
  // Pause/reprise réelle : on bascule seulement l'état `playing` ; l'effet dédié
  // appelle narration.pause()/resume() (conserve la position, pas de redémarrage).
  const togglePlay = () => {
    if (atEnd) {
      setEnded(false);
      setIndex(0);
      setPlaying(true);
      return;
    }
    setPlaying((p) => !p);
  };
  const replay = () => {
    setEnded(false);
    setIndex(0);
    setPlaying(true);
  };
  const jumpTo = (i) => {
    setEnded(false);
    setIndex(i);
    setPlaying(true);
  };
  const toggleMute = () => setMuted((m) => !m);

  // Change de langue : coupe la lecture en cours et rejoue depuis le début.
  const switchLang = (code) => {
    if (code === lang || !isLanguageAvailable(roleKey, code)) return;
    narration.stop();
    setEnded(false);
    setLang(code);
    setIndex(0);
    setPlaying(true);
  };

  const goRegister = () => {
    onClose();
    navigate('/register');
  };
  const goLogin = () => {
    onClose();
    navigate('/login');
  };

  return (
    <div className="rx-overlay" role="presentation" onClick={onClose}>
      <div
        className="rx-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rx-title"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="rx-header">
          <div className="rx-heading">
            <span className="rx-role-icon" aria-hidden="true">{content.icon}</span>
            <h2 id="rx-title" className="rx-title">{content.label}</h2>
          </div>
          <div className="rx-header-actions">
            <div className="rx-lang" role="group" aria-label={ui.chooseLang}>
              {LANGUAGES.map((l) => {
                const available = isLanguageAvailable(roleKey, l.code);
                const active = l.code === lang;
                return (
                  <button
                    key={l.code}
                    type="button"
                    className={`rx-lang-btn ${active ? 'is-active' : ''}`}
                    onClick={() => switchLang(l.code)}
                    disabled={!available}
                    aria-pressed={active}
                    aria-label={available ? l.label : ui.langSoon(l.label)}
                    title={available ? l.label : ui.langSoon(l.label)}
                  >
                    <span className="rx-lang-flag" aria-hidden="true">{l.flag}</span>
                    <span className="rx-lang-short">{l.short}</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="rx-close"
              onClick={onClose}
              aria-label={ui.close}
            >
              ×
            </button>
          </div>
        </header>

        <div
          className="rx-progress"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={index + 1}
          aria-label={ui.sceneProgress(index + 1, total)}
        >
          {scenes.map((s, i) => {
            const fill = i < index ? 1 : i === index ? narration.progress : 0;
            return (
              <button
                key={s.id}
                type="button"
                className={`rx-dot ${i === index ? 'is-active' : ''}`}
                onClick={() => jumpTo(i)}
                aria-label={ui.goScene(i + 1)}
                aria-current={i === index ? 'true' : undefined}
              >
                <span
                  className="rx-dot-fill"
                  style={{ transform: `scaleX(${fill})` }}
                />
              </button>
            );
          })}
        </div>

        <div className="rx-stage-wrap">
          <SceneCrossfade scene={scene} />
        </div>

        {/* Sous-titre karaoké visible (décoratif : se met à jour à chaque frame) */}
        <p className="rx-caption" aria-hidden="true">
          <KaraokeCaption
            key={`${roleKey}-${index}`}
            text={scene?.text}
            keyword={scene?.keyword}
            progress={narration.progress}
          />
        </p>
        {/* Annonce lecteur d'écran : une fois par scène (pas à chaque frame) */}
        <p className="sr-only" aria-live="polite">{scene?.text}</p>

        {narration.usingTimerFallback && (
          <p className="rx-audio-note" role="note">
            {ui.voiceFallback}
          </p>
        )}

        {atEnd ? (
          <div className="rx-cta">
            <button type="button" className="btn-primary btn-block" onClick={goRegister}>
              {ui.register}
            </button>
            <button type="button" className="btn-secondary btn-block" onClick={goLogin}>
              {ui.login}
            </button>
            <button type="button" className="rx-replay-link" onClick={replay}>
              {ui.replay}
            </button>
          </div>
        ) : (
          <div className="rx-controls" role="group" aria-label={ui.controls}>
            <button
              type="button"
              className="rx-ctrl"
              onClick={goPrev}
              disabled={index === 0}
              aria-label={ui.prev}
            >
              <span className="rx-ctrl-ico" aria-hidden="true">⏮</span>
            </button>
            <button
              type="button"
              className="rx-ctrl rx-ctrl-primary"
              onClick={togglePlay}
              aria-label={playing ? ui.pause : ui.play}
            >
              <span className="rx-ctrl-ico" aria-hidden="true">{playing ? '⏸' : '▶'}</span>
            </button>
            <button
              type="button"
              className="rx-ctrl"
              onClick={goNext}
              disabled={isLast}
              aria-label={ui.next}
            >
              <span className="rx-ctrl-ico" aria-hidden="true">⏭</span>
            </button>
            <button
              type="button"
              className="rx-ctrl"
              onClick={toggleMute}
              aria-pressed={muted}
              aria-label={muted ? ui.unmute : ui.mute}
            >
              <span className="rx-ctrl-ico" aria-hidden="true">{muted ? '🔇' : '🔊'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RoleExplainerModal;
